import { Autocomplete, useMantineTheme } from "@mantine/core";
import React, { ReactNode, RefObject, useMemo, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { useIntl } from "react-intl";
import { useMediaQuery } from "@mantine/hooks";
import classNames from "classnames";
import { getBreakpoint } from "@/utils/mantine";
import {
  FormValues,
  SelectedPlace,
  ThinBorderStyle,
  Trips,
} from "@/interfaces/searchBlock";
import { PlacesNotFound } from "./placesNotFound/PlacesNotFound";
import { useFetchAutocompleteAgencies } from "./hooks/useFetchAutocompleteAgencies";

import styles from "./PeliasAutocomplete.module.scss";
import { InputWithAutofocus } from "../../helpers/autoFocusNextInput";
import { ValidationMethods } from "../../validation/isFormValidAndDisplayErrors";
import AutoCompleteItem, {
  AutoCompleteItemProps,
  AutocompleteType,
} from "./autocompleteItem/AutocompleteItem";
import { blurInput } from "../../helpers/isInputFilled";
import { PeliasService } from "./hooks/usePeliasPlaces";

export type AutocompleteEvent = {
  target: {
    name: Trips;
    value?: SelectedPlace;
  };
  type: string;
};

type SearchBlockAutocompleteProps = {
  inputId: string;
  ariaLabel: string;
  placeholder: string;
  icon: ReactNode;
  thinBorder?: ThinBorderStyle;
  peliasService: PeliasService | null;
  name: Trips;
  control: Control<FormValues, Trips>;
  validationMethods: ValidationMethods;
  tripRef: RefObject<HTMLInputElement>;
  displayAgencyPropositions: boolean;
  autoFocusNextInput: (currentInput: InputWithAutofocus) => void;
};

export const PeliasAutocomplete = ({
  inputId,
  ariaLabel,
  placeholder,
  icon,
  thinBorder,
  peliasService,
  name,
  control,
  validationMethods,
  tripRef,
  displayAgencyPropositions,
  autoFocusNextInput,
}: SearchBlockAutocompleteProps) => {
  const intl = useIntl();
  const theme = useMantineTheme();
  const isTabletOrDesktop = useMediaQuery(getBreakpoint(theme.breakpoints.md));
  const isDesktop = useMediaQuery(getBreakpoint(theme.breakpoints.lg));
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");
  const autocompleteAgenciesProposition = useFetchAutocompleteAgencies(
    autocompleteInputValue
  );

  // Hide dropdown before modifications to avoid empty display
  const [isModified, setIsModified] = useState(false);

  const maxAgencyPropositions = isTabletOrDesktop ? 8 : 4;
  const maxAddressPropositions = 4;

  const peliasPlaces = peliasService?.peliasPlaces;
  const isPeliasLoading = peliasService?.isPeliasLoading ?? true;

  const autocompleteItems = useMemo(() => {
    let autocompleteAgenciesItem: AutoCompleteItemProps[] = [];
    if (autocompleteAgenciesProposition !== undefined) {
      const limitedAutocompleteAgenciesProposition =
        autocompleteAgenciesProposition.filter(
          (_, index) => index < maxAgencyPropositions
        );

      autocompleteAgenciesItem = limitedAutocompleteAgenciesProposition.map(
        (agenciesPrediction) => {
          return {
            value: agenciesPrediction.name,
            placeId: null,
            shouldDisplayGoogleLogo: false,
            type: AutocompleteType.AGENCY,
            group: intl.formatMessage({ id: AutocompleteType.AGENCY }),
            latitude: agenciesPrediction.gpsLat.toString(),
            longitude: agenciesPrediction.gpsLong.toString(),
          };
        }
      );
    }

    let peliasPlaceItems: AutoCompleteItemProps[] = [];
    if (peliasPlaces !== undefined) {
      const limitedPeliasPlaces = peliasPlaces.slice(0, maxAddressPropositions);
      peliasPlaceItems = limitedPeliasPlaces.map((peliasPlace) => {
        return {
          value: peliasPlace.description,
          key: peliasPlace.placeId,
          placeId: peliasPlace.placeId,
          shouldDisplayGoogleLogo: false,
          type: AutocompleteType.ADDRESS,
          group: intl.formatMessage({ id: AutocompleteType.ADDRESS }),
          latitude: peliasPlace.latitude.toString(),
          longitude: peliasPlace.longitude.toString(),
        };
      });
    }

    return [
      ...(displayAgencyPropositions ? autocompleteAgenciesItem : []),
      ...peliasPlaceItems,
    ];
  }, [
    peliasPlaces,
    autocompleteAgenciesProposition,
    maxAgencyPropositions,
    maxAddressPropositions,
    displayAgencyPropositions,
    intl,
  ]);

  const disableDefaultMantineExactFilter = () => true;

  const [autocompleteDelayed, setAutocompleteDelayed] =
    useState<boolean>(false);
  const [autocompleteTimeout, setAutocompleteTimeout] =
    useState<NodeJS.Timeout>();
  const delayAutocompleteValue = (event: string) => {
    if (autocompleteDelayed && autocompleteTimeout) {
      clearTimeout(autocompleteTimeout);
    }

    setAutocompleteDelayed(true);
    setAutocompleteTimeout(
      setTimeout(() => {
        setAutocompleteInputValue(event);
        setAutocompleteDelayed(false);
      }, 250)
    );
  };

  return (
    <Controller
      render={({ field, fieldState: { error } }) => {
        const shouldHideDropdown =
          isPeliasLoading ||
          (field.value?.description.length !== undefined &&
            field.value.description.length < 3) ||
          !isModified;

        const shouldDisplayError = error !== undefined;

        return (
          <Autocomplete
            {...field}
            ref={tripRef}
            value={field.value?.description}
            id={inputId}
            icon={icon}
            placeholder={placeholder}
            aria-label={ariaLabel}
            error={shouldDisplayError}
            onBlur={field.onBlur}
            dropdownPosition="bottom"
            limit={maxAgencyPropositions + maxAddressPropositions}
            filter={disableDefaultMantineExactFilter}
            wrapperProps={{ "data-cy": `autocomplete-${inputId}` }}
            styles={{
              dropdown: {
                maxHeight: isDesktop ? "" : "35rem",
                "& .mantine-Text-root": {
                  fontSize: 12,
                  color: "#0069b4",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  letterSpacing: "0.12rem",
                },
              },
            }}
            classNames={{
              input: classNames({
                [styles.input]: true,
                [styles.error]: shouldDisplayError,
                [styles.thinBorderLeft]:
                  thinBorder === ThinBorderStyle.THIN_BORDER_LEFT &&
                  isTabletOrDesktop,
                [styles.thinBorderRight]:
                  thinBorder === ThinBorderStyle.THIN_BORDER_RIGHT &&
                  isTabletOrDesktop,
              }),
              label: styles.label,
              hovered: styles.hovered,
              dropdown: shouldHideDropdown
                ? styles.dropdownHidden
                : styles.dropdownShown,
              icon: styles.icon,
            }}
            itemComponent={AutoCompleteItem}
            nothingFound={<PlacesNotFound />}
            variant="unstyled"
            onChange={(event) => {
              setIsModified(true);
              peliasService?.getPeliasPlaces(event);
              field.onChange({ description: event, placeId: null });
              delayAutocompleteValue(event);
            }}
            onItemSubmit={(selectedPlace: {
              placeId: string;
              value: string;
              latitude: string;
              longitude: string;
              type: string;
            }) => {
              field.onChange({
                description: selectedPlace.value,
                placeId: selectedPlace.placeId,
                latitude: selectedPlace.latitude,
                longitude: selectedPlace.longitude,
                isAgency: selectedPlace.type === AutocompleteType.AGENCY,
              });

              blurInput(tripRef);
              autoFocusNextInput(name);

              if (!isTabletOrDesktop) {
                tripRef.current?.scrollIntoView({
                  behavior: "smooth",
                });
              }
            }}
            data={autocompleteItems}
            onFocus={() => {
              if (field.value !== undefined) {
                peliasService?.getPeliasPlaces(field.value.description);
              }
            }}
          />
        );
      }}
      control={control}
      name={name}
      rules={{
        onChange: (event: AutocompleteEvent) => {
          validationMethods.clearErrors(event.target.name);
        },
      }}
    />
  );
};

PeliasAutocomplete.displayName = "SearchBlockTextInput";

export default PeliasAutocomplete;
